import { getSupabase } from '@/lib/supabase';
import type { ChatMessage, MessageThread, AuthorType } from '@/types/messages';
import { api } from '@/lib/apiClient';

const _sb = getSupabase();

const _T_THREADS = 'message_threads';
const _T_PARTS = 'message_thread_participants';
const _T_MSGS = 'messages';

// ---- Worker /threads cache (performance) ----
// The worker endpoint /api/messages/threads can be slow (1-6s). We heavily reuse it for
// getThreadByPair/getThread/getThreadsByCoach/getThreadsByClient, so cache + dedupe.
let threadsCache: any[] | null = null;
let threadsCacheAt = 0;
let threadsInFlight: Promise<any[] | null> | null = null;
const THREADS_CACHE_TTL_MS = 5000;

async function fetchThreadsCached(force = false): Promise<any[]> {
  const now = Date.now();
  if (!force && threadsCache && now - threadsCacheAt < THREADS_CACHE_TTL_MS) {
    return threadsCache;
  }
  if (threadsInFlight) {
    const res = await threadsInFlight;
    return res || [];
  }
  threadsInFlight = (async () => {
    try {
      const response = await fetch('/api/messages/threads', { credentials: 'include' });
      if (!response.ok) return [];
      const responseData = await response.json();
      // Handle both legacy format { threads } and new format { ok, data: { threads } }
      const threads = responseData.data?.threads ?? responseData.threads;
      const arr = Array.isArray(threads) ? threads : [];
      threadsCache = arr;
      threadsCacheAt = Date.now();
      return arr;
    } catch {
      return [];
    } finally {
      threadsInFlight = null;
    }
  })();
  const res = await threadsInFlight;
  return res || [];
}

type MessagesStore = {
  getThreadsByCoach(coachId: string): Promise<MessageThread[]>;
  getThreadsByClient(clientId: string): Promise<MessageThread[]>;
  getThreadByPair?(clientId: string, coachId: string): Promise<MessageThread | null>;
  getThread(id: string): Promise<MessageThread | null>;
  createThread(clientId: string, coachId: string): Promise<MessageThread>;
  getMessages(threadId: string): Promise<ChatMessage[]>;
  appendMessage(msg: ChatMessage): Promise<void>;
  markRead(threadId: string, role: AuthorType, userId: string, atISO?: string): Promise<void>;
  getUnreadCounts(target: { coachId?: string; clientId?: string }): Promise<number>;
};

export const supabaseMessagesStore: MessagesStore = {
  async getThreadsByCoach(coachId) {
    // Use Worker endpoint for httpOnly cookie auth
    try {
      const threads = await fetchThreadsCached();
      // Filter for coach (worker returns all threads for current user)
      return (threads || []).filter((t: any) => t.coachId === coachId);
    } catch (error) {
      console.error('[messages] getThreadsByCoach failed:', error);
      return [];
    }
  },
  async getThreadsByClient(clientId) {
    // Use Worker endpoint for httpOnly cookie auth
    try {
      const threads = await fetchThreadsCached();
      // Filter for client (worker returns all threads for current user)
      return (threads || []).filter((t: any) => t.clientId === clientId);
    } catch (error) {
      console.error('[messages] getThreadsByClient failed:', error);
      return [];
    }
  },
  async getThread(id) {
    // Use Worker endpoint
    try {
      const threads = await fetchThreadsCached();
      return (threads || []).find((t: any) => t.id === id) || null;
    } catch (error) {
      console.error('[messages] getThread failed:', error);
      return null;
    }
  },
  async getThreadByPair(clientId, coachId) {
    // Use Worker endpoint
    try {
      const threads = await fetchThreadsCached();
      return (threads || []).find((t: any) => t.clientId === clientId && t.coachId === coachId) || null;
    } catch (error) {
      console.error('[messages] getThreadByPair failed:', error);
      return null;
    }
  },
  async createThread(clientId, coachId) {
    try {
      const res = await api.post<unknown>('/api/messages/threads', { clientId, coachId });

      if (!res.ok) {
        const error = (res.body ?? {}) as { error?: string };
        console.error('[messages] createThread error:', res.body ?? res.error);
        throw new Error(error.error || 'Failed to create thread');
      }

      const responseData = res as { data?: { thread?: any }; thread?: any };
      const thread = responseData.data?.thread ?? responseData.thread;

      if (!thread || !thread.id) {
        console.error('[messages] createThread returned invalid thread');
        throw new Error('Invalid thread returned from server');
      }

      return thread;
    } catch (error) {
      console.error('[messages] createThread failed:', error);
      throw error;
    }
  },
  async getMessages(threadId) {
    // Use Worker endpoint
    try {
      const response = await fetch(`/api/messages/${threadId}`, {
        credentials: 'include',
      });
      if (!response.ok) return [];
      // OBS-11 a-ignore: an unparseable body becomes null, which the !ok / missing-field check right after handles.
      const payload = await response.json().catch(() => null);
      // Handle both legacy format { messages } and new format { ok, data: { messages } }
      return payload?.data?.messages ?? payload?.messages ?? [];
    } catch (error) {
      console.error('[messages] getMessages failed:', error);
      return [];
    }
  },
  async appendMessage(msg) {
    // Use Worker endpoint
    try {
      const res = await api.post(`/api/messages/${msg.threadId}`, {
        messageBody: msg.body,
        authorType: msg.authorType,
      });

      if (!res.ok) {
        const error = (res.body ?? {}) as { error?: string };
        throw new Error(error.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('[messages] appendMessage failed:', error);
      throw error;
    }
  },
  async markRead(threadId, role, _userId, _atISO) {
    // Use Worker endpoint
    try {
      const res = await api.put(`/api/messages/${threadId}`, { role });

      if (!res.ok) {
        const error = (res.body ?? {}) as { error?: string };
        throw new Error(error.error || 'Failed to mark as read');
      }
    } catch (error) {
      console.error('[messages] markRead failed:', error);
      throw error;
    }
  },
  async getUnreadCounts({ coachId, clientId }) {
    // Use Worker endpoint to get threads with unread counts
    try {
      const response = await fetch('/api/messages/threads', {
        credentials: 'include',
      });
      if (!response.ok) return 0;
      const responseData = await response.json();
      // Handle both legacy format { threads } and new format { ok, data: { threads } }
      const threads = responseData.data?.threads ?? responseData.threads;

      // Sum up unread counts from threads
      let total = 0;
      if (coachId) {
        // For coach: sum unreadForCoach from all threads
        total = (threads || []).reduce((sum: number, t: any) => sum + (t.unreadForCoach || 0), 0);
      } else if (clientId) {
        // For client: sum unreadForClient from all threads
        total = (threads || []).reduce((sum: number, t: any) => sum + (t.unreadForClient || 0), 0);
      }

      return total;
    } catch (error) {
      console.error('[messages] getUnreadCounts failed:', error);
      return 0;
    }
  },
};
